import {useState, useEffect, forwardRef} from "react";
import {Link} from "react-router-dom";
import {cx, formatMsAsSec, shrinker, useGlobalKeyPress} from "@cdx/common";
import {cardStatusBaseTheme, cardStatusVariants} from "@cdx/ds/components/DSCard/DSCardTheme.css";
import {DSIconButton, DSIcon, Row, css, Text, Box} from "@cdx/ds";
import {layoutStyles} from "@cdx/ds/css/layout.css";
import {toMs} from "@cdx/shared/duration";
import {pronounceSafeSeq} from "@cdx/shared/sequences";
import {api} from "../../lib/api";
import type {CardStatus} from "../../lib/card-status-utils";
import {getCardThemeStatus} from "../../lib/card-status-utils";
import useDebounceActions from "../../lib/hooks/useDebounceActions";
import useOnKeepDown from "../../lib/hooks/useOnKeepDown";
import useRouteDependentCardLink from "../../lib/hooks/useRouteDependentCardLink";
import {getParentCard} from "../workflows/workflow-utils";
import type {Card} from "../../cdx-models/Card";
import type {User, UserId} from "../../cdx-models/User";
import type {TimeTrackingSegment} from "../../cdx-models/TimeTrackingSegment";
import type {ActiveTimeTracker} from "../../cdx-models/ActiveTimeTracker";
import type {Root} from "../../cdx-models/Root";
import {useTitlePrefix} from "../../components/useTitle";
import {getTotalTimeForTimeTrackingSum} from "./time-tracking-utils";
import {timerWidgetStyles as styles} from "./time-tracking.css";

export const useUpdatingTime = (time: number) => {
  const [, update] = useState(0);
  useEffect(() => {
    const msPart = Math.abs(time) % 1000;
    const id = setTimeout(() => update((u) => u + 1), 1001 - msPart);
    return () => clearTimeout(id);
  }, [time]);
};

export const ControlButton = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => <button className={styles.controlButton} ref={ref} {...props} />);

const StoppedTimerControl = ({
  status: cardStatus,
  card,
  user,
}: {
  status: CardStatus;
  card: Card;
  user: User;
}) => {
  const lastSegment = card.$meta.first("timeTrackingSegments", {
    userId: user.id as UserId,
    $order: "-createdAt",
  });
  const status = lastSegment?.autoFinishedState === "auto_finished" ? "snoozing" : cardStatus;
  const userId = user.$meta.get("id", null);
  const cardId = card.$meta.get("cardId", null);
  return (
    <Row
      rounded={4}
      className={cx(cardStatusBaseTheme, cardStatusVariants[status])}
      bg
      pa="4px"
      sp="4px"
      align="center"
    >
      <Row align="baseline" sp="4px" px="4px">
        <Text type="label11Caps" color="secondary">
          Total
        </Text>
        <Text color="primary" type="label12light" variantNumeric="tabularNums">
          {formatMsAsSec(
            user.$meta.isLoaded
              ? getTotalTimeForTimeTrackingSum(
                  card.$meta.first("userTimeTrackingSums", {
                    userId: user.id as UserId,
                    $order: "userId",
                  })
                )
              : null
          )}
        </Text>
      </Row>
      <ControlButton
        onClick={() => {
          if (!userId || !cardId) return;
          void api.mutate.timeTracking.start({userId, cardId});
        }}
      >
        <DSIcon name="Play" label="Start" />
      </ControlButton>
    </Row>
  );
};

export const useTimer = ({segment, time}: {segment: TimeTrackingSegment; time: number}) => {
  const [updatedBackendVal, setBackendVal] = useState(0);
  const loadedBackendVal = segment.$meta.isFieldLoaded("modifyDurationMsBy")
    ? segment.modifyDurationMsBy
    : updatedBackendVal;

  const segmentId = segment.$meta.get("id", null);
  const sendModifyTime = (val: number) => {
    if (!val || !segmentId) return;
    const targetVal = Math.round(loadedBackendVal + toMs.minutes(val));
    setBackendVal(targetVal);
    return api.mutate.timeTracking.setModifyDuration({
      id: segmentId,
      modifyDurationMsBy: targetVal,
    });
  };

  const [setModifyTimeBy, debouncedVal] = useDebounceActions({
    initialVal: 0,
    fn: sendModifyTime,
  });

  const shownModifyTimeMs = loadedBackendVal + toMs.minutes(debouncedVal);

  const minusHandlers = useOnKeepDown(() =>
    setModifyTimeBy((prevMins: number) => {
      const currMins = (time + loadedBackendVal) / 60 / 1000;
      return currMins + prevMins - 5 > 0 ? prevMins - 5 : -currMins;
    })
  );
  const plusHandlers = useOnKeepDown(() => setModifyTimeBy((t: number) => t + 5));

  return {
    setModifyTimeBy,
    shownModifyTimeMs,
    debouncedVal,
    sendModifyTime,
    minusHandlers,
    plusHandlers,
  };
};

export const RunningTimer = ({
  segment,
  disabled,
}: {
  segment: TimeTrackingSegment;
  disabled?: boolean;
}) => {
  const getRunningMs = () => {
    const startedAt = segment.$meta.get("startedAt", null);
    if (!startedAt) return 0;
    const runningMs =
      (segment.$meta.get("finishedAt", null) || new Date()).getTime() - startedAt.getTime();
    return runningMs;
  };
  const time = getRunningMs();
  const {
    shownModifyTimeMs,
    setModifyTimeBy,
    debouncedVal,
    sendModifyTime,
    minusHandlers,
    plusHandlers,
  } = useTimer({segment, time});
  const segmentId = segment.$meta.get("id", null);

  useUpdatingTime(time);

  useTitlePrefix(time > 0 ? `[${formatMsAsSec(time + shownModifyTimeMs, {short: true})}] •` : null);

  const handleStop = () => {
    if (!segmentId) return;
    if (debouncedVal) {
      const p = sendModifyTime(debouncedVal)!.then(() =>
        api.mutate.timeTracking.stop({id: segmentId})
      );
      setModifyTimeBy(() => 0);
      return p;
    } else {
      return api.mutate.timeTracking.stop({id: segmentId});
    }
  };

  const status = "started";

  return (
    <Row
      rounded="sm"
      className={cx(cardStatusBaseTheme, cardStatusVariants[status])}
      bg
      pa="4px"
      sp="4px"
      align="center"
    >
      <ControlButton disabled={disabled} {...minusHandlers}>
        <DSIcon name="Minus" label="Reduce by 5 minutes" />
      </ControlButton>
      <Text type="label12light" variantNumeric="tabularNums" color="primary">
        {formatMsAsSec(time + shownModifyTimeMs, {short: true})}
      </Text>
      <ControlButton disabled={disabled} {...plusHandlers}>
        <DSIcon name="Plus" label="Add 5 minutes" />
      </ControlButton>
      <ControlButton disabled={disabled} onClick={() => void handleStop()}>
        <DSIcon name="Stop" label="Stop" />
      </ControlButton>
    </Row>
  );
};

const CardTimer = ({att, user}: {att: ActiveTimeTracker; user: User}) => {
  const {card} = att;
  const runningSegment = card.$meta.first("timeTrackingSegments", {
    userId: user.id as UserId,
    finishedAt: null,
    $order: "createdAt",
  });
  const status = getCardThemeStatus(card.derivedStatus);
  const cardLink = useRouteDependentCardLink(card);
  const parentCard = getParentCard(card);
  const pTitle = parentCard && parentCard.$meta.get("title", null);
  const userId = user.$meta.get("id", null);
  const cardId = card.$meta.get("cardId", null);
  const runningSegmentId = runningSegment?.$meta.get("id", null) ?? null;

  useGlobalKeyPress({
    fn: () => {
      if (runningSegmentId) {
        void api.mutate.timeTracking.stop({id: runningSegmentId});
      } else if (userId && cardId) {
        void api.mutate.timeTracking.start({userId, cardId});
      }
    },
    code: "KeyP",
    description: "Start or Stop Timer",
    category: null,
    withAlt: true,
  });

  return (
    <Row sp="8px" align="center" flex="auto">
      {runningSegment ? (
        <RunningTimer segment={runningSegment} />
      ) : (
        <StoppedTimerControl card={card} user={user} status={status} />
      )}
      <Row sp="4px" align="baseline" relative top="-1px">
        <Text type="label12light" color="secondary">
          on
        </Text>
        <Link
          to={cardLink}
          className={css({color: "primary", display: "flex", sp: "4px", align: "baseline"})}
        >
          {user.showCardIdInTimer && (
            <Text as="span" color="secondary" type="label12">
              ${pronounceSafeSeq.intToSeq(card.accountSeq)}
            </Text>
          )}
          {pTitle && (
            <Box
              colorTheme="gray850"
              bg
              rounded={4}
              px="4px"
              py="2px"
              color="secondary"
              textType="label11"
            >
              {shrinker(pTitle, 16)}
            </Box>
          )}
          <Text type="label14light">{shrinker(card.title, 20)}</Text>
        </Link>
      </Row>
      {!runningSegment && (
        <DSIconButton
          variant="tertiary"
          size="sm"
          icon={<DSIcon name="Close" label="Close" />}
          onClick={() => {
            const attId = att.$meta.get("id", null);
            if (!attId) return;
            return api.mutate.timeTracking.close({id: attId});
          }}
          negatePadding
          className={layoutStyles.ml.auto}
        />
      )}
    </Row>
  );
};

const TimerWidget = ({root}: {root: Root}) => {
  const user = root.loggedInUser;
  if (!user) return null;
  const att = user.activeTimeTracker;

  if (!att || !att.$meta.isLoaded || !user.$meta.isLoaded || !att.card) return null;

  return (
    <Row
      position="fixed"
      className={styles.container}
      py="4px"
      sp="8px"
      px="8px"
      align="center"
      colorTheme="gray750"
    >
      <DSIcon name="StopWatch" label="Tracked time" size={16} className={css({color: "primary"})} />
      <CardTimer att={att} user={user} />
    </Row>
  );
};

export default TimerWidget;
